<template>
    <!-- For Item in client site -->
    <template v-if="item.isClientSite">
        <template v-for="(subItem, idx) in item.subItems" :key="subItem.value || idx">
            <v-list-item
                v-if="isVisible(subItem)"
                :title="props.isRailed ? undefined : t(subItem.title)"
                :value="subItem.value"
                :to="{ name: subItem.name, params: route?.params || {} }"
                @click="handleClick"
            >
                <template v-slot:prepend>
                    <AQIcon
                        v-if="subItem.prependIcon"
                        :name="subItem.prependIcon"
                        :color="Colors.aquassistWhite"
                    />
                    <img
                        v-if="subItem.img"
                        width="20px"
                        height="20px"
                        class="filter--white mr-4"
                        :src="resolveImg(subItem.img)"
                        alt=""
                    />
                </template>
                <template v-if="subItem.badge" v-slot:append>
                    <v-badge
                        v-if="store.getters[`siteOrders/${subItem.badge}`] > 0"
                        :content="store.getters[`siteOrders/${subItem.badge}`]"
                        color="aquassistRed"
                        inline
                    />
                </template>
            </v-list-item>
        </template>
    </template>
    <template v-else>
        <!-- For items with children  -->
        <template v-if="item.hasChildren || (item.subItems && item.subItems.length)">
            <v-list-item
                :title="t(item.title)"
                :to="{ name: item.name, params: route?.params || {} }"
                @click="toggleExpanded"
                class="sidebar-parent-item"
            >
                <template v-slot:prepend>
                    <AQIcon
                        v-if="item.prependIcon"
                        :name="item.prependIcon"
                        :color="Colors.aquassistWhite"
                        size="small"
                    />
                    <img
                        v-if="item.img"
                        width="20px"
                        height="20px"
                        class="filter--white mr-4"
                        :src="resolveImg(item.img)"
                        alt=""
                    />
                </template>

                <template v-slot:append>
                    <v-icon
                        :icon="isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                        color="white"
                        size="small"
                    />
                </template>
            </v-list-item>

            <!-- Collapsible children -->
            <v-expand-transition>
                <div v-if="isExpanded" class="sidebar-children">
                    <SidebarItem
                        v-for="(subItem, idx) in item.subItems"
                        :key="subItem.value || idx"
                        :item="subItem"
                        :is-railed="props.isRailed"
                        @item-click="handleSubItemClick"
                    />
                </div>
            </v-expand-transition>
        </template>
        <!-- For header type items (legacy support) -->
        <template v-else-if="item.type === 'header'">
            <v-list-item>
                <v-expansion-panels variant="accordion">
                    <v-expansion-panel>
                        <v-expansion-panel-title>
                            <AQIcon
                                v-if="item.prependIcon"
                                :name="item.prependIcon"
                                :color="Colors.aquassistWhite"
                            />
                            <span v-if="!props.isRailed" class="ml-2">{{ t(item.title) }}</span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-fade-transition leave-absolute>
                                <v-list>
                                    <SidebarItem
                                        v-for="(subItem, idx) in item.subItems"
                                        :key="subItem.value || idx"
                                        :item="subItem"
                                        :is-railed="props.isRailed"
                                        @item-click="handleSubItemClick"
                                    />
                                </v-list>
                            </v-fade-transition>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-list-item>
        </template>
        <!-- For divider-only support -->
        <template v-else-if="item.isDivider">
            <v-list-item>
                <span class="ml-2">{{ t(item.title) }}</span>
            </v-list-item>
            <v-divider class="customDivider" />
        </template>
        <!-- For regular items without children -->
        <template v-else>
            <v-list-item
                v-if="isVisible(item)"
                :key="item.value"
                :title="props.isRailed ? undefined : t(item.title)"
                :value="item.value"
                :to="{ name: item.name, params: route?.params || {} }"
                @click="handleClick"
            >
                <template v-slot:prepend>
                    <AQIcon
                        v-if="item.prependIcon"
                        :name="item.prependIcon"
                        :color="Colors.aquassistWhite"
                    />
                    <img
                        v-if="item.img"
                        width="20px"
                        height="20px"
                        class="filter--white mr-4"
                        :src="resolveImg(item.img)"
                        alt=""
                    />
                </template>
            </v-list-item>
        </template>
    </template>
</template>

<script setup lang="ts">
    import { useI18n } from 'vue-i18n';
    import { ref, watch } from 'vue';
    import { useStore } from 'vuex';
    import { useRoute } from 'vue-router';
    import type { PropType } from 'vue';
    import { Colors } from '@/public/styles/utils/variables';

    const { t } = useI18n();

    /**
     * Sidebar icons have historically been bare filenames living in the shell's own
     * `@/assets/images/sidebar_icons/`. A submodule (e.g. `src/crm`) keeps its icons in its own
     * `src/assets/` and imports them in its route file, so `img` arrives here already resolved by
     * webpack — either a URL or an inlined `data:` URI. Pass those straight through; keep requiring
     * bare filenames from the shell folder so every existing nav item is unaffected.
     */
    const resolveImg = (img: string): string =>
        img.includes('/') || img.startsWith('data:')
            ? img
            : require(`@/assets/images/sidebar_icons/${img}`);
    const route = useRoute();
    const store = useStore();

    const isVisible = (candidate: SidebarItem): boolean =>
        !candidate.hidden && (!candidate.superAdmin || store.getters.superAdmin === true);

    export interface SidebarItem {
        type?: 'header' | 'item';
        title: string;
        value: string;
        path?: string;
        name?: string;
        prependIcon?: string;
        img?: string;
        hasChildren?: boolean;
        isClientSite?: boolean;
        hidden?: boolean;
        superAdmin?: boolean;
        subItems?: SidebarItem[];
        badge?: string;
        allowedRoles?: string[];
        isDivider?: boolean;
    }

    const props = defineProps({
        item: {
            type: Object as PropType<SidebarItem>,
            required: true,
        },
        isRailed: {
            type: Boolean,
        },
    });

    const emit = defineEmits<{
        (e: 'item-click'): void;
    }>();

    // Simple reactive state for this item's expansion
    const isExpanded = ref(false);

    // Watch for route changes to auto-expand items based on the current route
    watch(
        () => route?.path || '',
        (currentPath) => {
            if (!currentPath) return;

            // Check if this route or any child routes are active
            const shouldExpand = props.item.path && currentPath.startsWith(props.item.path);

            // Check subitems if not a direct match
            if (!shouldExpand && props.item.subItems) {
                const hasActiveChild = props.item.subItems.some(
                    (subItem) => subItem.path && currentPath.startsWith(subItem.path),
                );
                isExpanded.value = hasActiveChild;
            } else {
                isExpanded.value = Boolean(shouldExpand);
            }
        },
        { immediate: true },
    );

    const handleClick = () => {
        emit('item-click');
    };

    const toggleExpanded = () => {
        isExpanded.value = !isExpanded.value;
    };

    const handleSubItemClick = () => {
        emit('item-click');
    };
</script>
